/**
 * Simplified API handler - no automatic namespace loading
 * Namespace loading is now explicit via builder configuration
 */

import { EventEmitter } from '../base/event-emitter.js';
import { NetworkRepositoryConfig, PreloadNamespaces } from '../config';
import type { ContentUnit } from '../content/types.js';
import {
  isRetryableError,
  TranslationLockedError,
  TranslationNotFoundError,
} from '../errors/translation-errors.js';
import { IAsyncHandler } from '../interfaces/handler.interface.js';
import { IAsyncRepository } from '../interfaces/repository.interface.js';
import { NetworkRepository } from '../repositories/network-repository.js';
import type { TranslationData } from '../types/translation-data.types.js';
import type {
  NamespaceResult,
  TranslationRequest,
  TranslationResult,
} from '../types/translation.types.js';
import { logger, resolveNestedKey } from '../utils/index.js';

/**
 * Simple API handler - only loads individual keys
 * Namespace loading is handled explicitly by builder
 */
export class NetworkHandler implements IAsyncHandler {
  repository: IAsyncRepository;
  readonly name: string;
  readonly priority: number;
  readonly version = '1.0.0';
  readonly type = 'network' as const;
  readonly enabled: boolean;
  private bus: EventEmitter;

  private retryCount: number;
  private retryDelay: number;

  constructor(eventEmitter: EventEmitter, repository: IAsyncRepository) {
    this.bus = eventEmitter;
    this.repository = repository;
    this.name = repository.name;
    const config = repository.getConfig() as NetworkRepositoryConfig;
    this.enabled = config.enabled ?? false;
    this.priority = config.priority ?? 0;
    this.retryCount = config.options?.retries ?? 3;
    this.retryDelay = config.options?.retryDelay ?? 1000;
  }

  initialize(): Promise<void> {
    return Promise.resolve();
  }

  getSourceType(): 'memory' | 'storage' | 'network' | 'fallback' {
    return 'network';
  }

  /**
   * ✅ Lookup translation with full error handling
   * Strategy: Key first, namespace as fallback
   * Returns TranslationResult with status, source, duration, styles, and optional error
   */
  async lookup(request: TranslationRequest): Promise<TranslationResult> {
    const startTime = Date.now();
    const fallbackValue = request.parsedDefaultValue || request.cleanKey;

    if (!this.enabled) {
      return this.createTranslationResult(
        fallbackValue,
        'fallback',
        0,
        startTime
      );
    }

    const networkBus = this.bus.network(request, this.name);
    networkBus.start();
    logger.debug(`Network lookup: ${request.cleanKey}`);

    // Strategy 1: Try individual key lookup first (returns ContentUnit with styles)
    try {
      const contentUnit = await this.lookupContentUnitWithRetry(
        request.individualCacheKey
      );

      logger.debug(`Network hit: ${request.cleanKey}`);
      networkBus.success(contentUnit.text);

      // Return text + styles for caching
      return this.createTranslationResult(
        contentUnit.text,
        'network',
        200,
        startTime,
        undefined,
        false,
        undefined,
        contentUnit.styles
      );
    } catch (error) {
      // Handle 404 - key not found, try namespace fallback
      if (error instanceof TranslationNotFoundError && request.namespace) {
        logger.debug(
          `Individual key not found, trying namespace: ${request.cleanKey}`
        );
        return this.lookupFromNamespace(request, fallbackValue, startTime, networkBus);
      }

      // Handle 423 - locked translation
      if (error instanceof TranslationLockedError) {
        logger.debug(`Translation locked (423): ${request.cleanKey}`);
        return this.handleTranslationLocked(
          request,
          fallbackValue,
          error,
          startTime,
          networkBus
        );
      }

      // Handle other errors (server errors, network errors)
      logger.debug(`Network error: ${request.cleanKey}`, error);
      networkBus.error(error as Error);

      return this.createTranslationResult(
        fallbackValue,
        'fallback',
        0,
        startTime,
        error as Error
      );
    }
  }

  /**
   * Strategy 2: Namespace fallback when individual key not found
   */
  private async lookupFromNamespace(
    request: TranslationRequest,
    fallbackValue: string,
    startTime: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    networkBus: any
  ): Promise<TranslationResult> {
    try {
      const namespaceResult = await this.loadNamespace(
        request.locale,
        request.namespace!
      );

      if (namespaceResult && namespaceResult.data) {
        const nestedValue = resolveNestedKey(
          namespaceResult.data,
          request.cleanKey
        );

        if (nestedValue !== undefined && nestedValue !== null) {
          // Handle both ContentUnit format and plain strings
          let resolvedValue: string;
          let styles: Record<string, string[]> = {};

          if (typeof nestedValue === 'object' && nestedValue !== null && 'text' in nestedValue) {
            // ContentUnit format: { text, styles }
            const contentUnit = nestedValue as { text: unknown; styles?: Record<string, string[]> };
            resolvedValue = String(contentUnit.text);
            styles = contentUnit.styles || {};
          } else {
            // Plain string
            resolvedValue = String(nestedValue);
          }

          logger.debug(`Network namespace hit: ${request.cleanKey}, hasStyles=${Object.keys(styles).length > 0}`);
          networkBus.success(resolvedValue);

          return this.createTranslationResult(
            resolvedValue,
            'network',
            200,
            startTime,
            undefined,
            false,
            {
              locale: request.locale,
              namespace: request.namespace!,
              data: namespaceResult.data,
            },
            styles
          );
        }
      }

      // Key not found in namespace either
      const error = new TranslationNotFoundError(request.cleanKey);
      return this.handleTranslationNotFound(
        request,
        fallbackValue,
        error,
        startTime
      );
    } catch (nsError) {
      // Namespace load failed
      logger.debug(`Namespace load failed: ${request.cleanKey}`, nsError);

      if (nsError instanceof TranslationNotFoundError) {
        return this.handleTranslationNotFound(
          request,
          fallbackValue,
          nsError,
          startTime
        );
      }

      if (nsError instanceof TranslationLockedError) {
        return this.handleTranslationLocked(
          request,
          fallbackValue,
          nsError,
          startTime,
          networkBus
        );
      }

      networkBus.error(nsError as Error);
      return this.createTranslationResult(
        fallbackValue,
        'fallback',
        0,
        startTime,
        nsError as Error
      );
    }
  }

  /**
   * ✅ Load entire namespace (used for preloading)
   * Returns NamespaceResult with status and error information
   */
  async loadNamespace(
    locale: string,
    namespace: string
  ): Promise<NamespaceResult | null> {
    const startTime = Date.now();

    if (!this.enabled) {
      return this.createNamespaceResult(null, 'fallback', 0, startTime);
    }

    try {
      const data = await this.loadNamespaceWithRetry(locale, namespace);

      // 304 Not Modified — cached translations are still fresh
      if (data === null) {
        logger.debug(`Network namespace not modified: ${locale}:${namespace}`);
        return this.createNamespaceResult(null, 'network', 304, startTime);
      }

      logger.debug(`Network namespace loaded: ${locale}:${namespace}`);
      return this.createNamespaceResult(data, 'network', 200, startTime);
    } catch (error) {
      if (error instanceof TranslationLockedError) {
        logger.debug(`Namespace locked (423): ${locale}:${namespace}`);

        return this.createNamespaceResult(
          null,
          'fallback',
          423,
          startTime,
          error
        );
      }

      if (error instanceof TranslationNotFoundError) {
        logger.debug(`Network namespace not found: ${locale}:${namespace}`);

        return this.createNamespaceResult(
          null,
          'fallback',
          404,
          startTime,
          error
        );
      }

      logger.debug(`Network namespace error: ${locale}:${namespace}`, error);

      return this.createNamespaceResult(
        null,
        'fallback',
        0,
        startTime,
        error as Error
      );
    }
  }

  /**
   * ✅ Namespace lookup with retry logic
   * Throws custom errors (TranslationNotFoundError, TranslationLockedError, etc.)
   */
  private async loadNamespaceWithRetry(
    locale: string,
    namespace: string
  ): Promise<TranslationData | null> {
    return this.withRetry(
      () => this.repository.getNamespace(locale, namespace),
      `${locale}:${namespace}`,
      'namespace request'
    );
  }

  /**
   * Simple save implementation (API usually doesn't save)
   */
  async save(
    request: TranslationRequest,
    result: TranslationResult
  ): Promise<void> {
    // APIs usually don't save translations, but we could implement PUT if needed
    void request;
    void result;
  }

  /**
   * ✅ Lookup with retry logic for individual keys
   * Throws custom errors (TranslationNotFoundError, TranslationLockedError, etc.)
   */
  private async lookupWithRetry(cacheKey: string): Promise<string> {
    return this.withRetry(
      () => this.repository.get(cacheKey),
      cacheKey,
      'network request'
    );
  }

  /**
   * ✅ Lookup ContentUnit with retry logic for individual keys
   * Returns full ContentUnit with text and styles
   * Throws custom errors (TranslationNotFoundError, TranslationLockedError, etc.)
   */
  private async lookupContentUnitWithRetry(cacheKey: string): Promise<ContentUnit> {
    // Check if repository supports getContentUnit (NetworkRepository does)
    const repo = this.repository as NetworkRepository;
    if (typeof repo.getContentUnit !== 'function') {
      // Fallback: use get() and wrap in ContentUnit
      const text = await this.lookupWithRetry(cacheKey);
      return { text, styles: {} };
    }

    return this.withRetry(
      () => repo.getContentUnit(cacheKey),
      cacheKey,
      'network content unit request'
    );
  }

  /**
   * Delay utility for retries
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private calculateBackoff(attempt: number): number {
    const baseDelay = this.retryDelay;
    const exponentialDelay = baseDelay * Math.pow(2, attempt - 1);
    const maxDelay = 10000; // 10 seconds max

    // Add jitter to prevent thundering herd
    const jitter = Math.random() * 0.1 * exponentialDelay;

    return Math.min(exponentialDelay + jitter, maxDelay);
  }

  /**
   * Generic retry wrapper for repository operations
   * Handles retryable errors with exponential backoff
   */
  private async withRetry<T>(
    operation: () => Promise<T>,
    contextKey: string,
    contextDescription: string,
    attempt = 1
  ): Promise<T> {
    const networkBus = this.bus.network(contextKey, 'withRetry');

    try {
      return await operation();
    } catch (error) {
      // Retry only on retryable errors (5xx, network errors)
      if (attempt <= this.retryCount && isRetryableError(error)) {
        const delay = this.calculateBackoff(attempt);
        networkBus.retry(attempt, delay, (error as Error).message);
        await this.delay(delay);

        logger.debug(
          `Retrying ${contextDescription} (${attempt}/${this.retryCount}) after ${delay}ms`
        );
        return this.withRetry(
          operation,
          contextKey,
          contextDescription,
          attempt + 1
        );
      }

      // Don't retry client errors (404, 423)
      throw error;
    }
  }

  /**
   * Handle translation not found (after namespace fallback also failed)
   */
  private handleTranslationNotFound(
    request: TranslationRequest,
    fallbackValue: string,
    error: TranslationNotFoundError,
    startTime: number
  ): TranslationResult {
    logger.debug(`Network namespace miss: ${request.cleanKey}`);
    const translateBus = this.bus.translation(request);
    translateBus.notFound(
      fallbackValue,
      request.locale,
      request.namespace || 'default'
    );

    return this.createTranslationResult(
      fallbackValue,
      'fallback',
      404,
      startTime,
      error
    );
  }

  /**
   * Handle TranslationLockedError for translation lookups
   */
  private handleTranslationLocked(
    request: TranslationRequest,
    fallbackValue: string,
    error: TranslationLockedError,
    startTime: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    networkBus: any
  ): TranslationResult {
    const translateBus = this.bus.translation(request);
    translateBus.locked(
      fallbackValue,
      request.locale,
      request.namespace || 'default'
    );
    networkBus.error(error, 423);

    return this.createTranslationResult(
      fallbackValue,
      'fallback',
      423,
      startTime,
      error
    );
  }

  /**
   * Helper to create consistent TranslationResult objects
   */
  private createTranslationResult(
    value: string,
    source: 'network' | 'fallback' | 'storage' | 'memory',
    status: number,
    startTime: number,
    error?: Error,
    cached?: boolean,
    namespaceLoaded?: {
      locale: string;
      namespace: string;
      data: TranslationData;
    },
    styles?: Record<string, string[]>
  ): TranslationResult {
    return {
      value,
      source,
      status,
      duration: Date.now() - startTime,
      ...(error && { error }),
      ...(cached !== undefined && { cached }),
      ...(namespaceLoaded && { namespaceLoaded }),
      ...(styles && Object.keys(styles).length > 0 && { styles }),
    };
  }

  /**
   * Helper to create consistent NamespaceResult objects
   */
  private createNamespaceResult(
    data: TranslationData | null,
    source: 'network' | 'fallback' | 'storage' | 'memory',
    status: number,
    startTime: number,
    error?: Error
  ): NamespaceResult {
    return {
      data,
      source,
      status,
      duration: Date.now() - startTime,
      ...(error && { error }),
    };
  }

  getPreload(): Array<PreloadNamespaces> {
    const config = this.repository.getConfig() as NetworkRepositoryConfig;
    return config.options?.preloadNamespaces || [];
  }

  /**
   * The host this handler talks to. Exposed so a failure can NAME it: "the
   * server is unreachable" is not actionable, and a default pointing at a dead
   * host is exactly the case where the reader needs to be told which host.
   */
  getBaseUrl(): string {
    const config = this.repository.getConfig() as NetworkRepositoryConfig;
    return config.options?.baseUrl || '';
  }
}
